<template>
  <div
    v-if="shouldShow"
    class="banner-base"
    :class="bannerClass"
  >
    <div class="banner-content">
      <span class="icon-tiny">{{ config.icon }}</span>
      <span class="banner-message">
        {{ config.message }}
      </span>
      <button
        v-if="config.action !== 'none'"
        class="btn-action btn-action-sm"
        :class="buttonClass"
        @click="handleAction"
      >
        {{ config.buttonText }}
      </button>
      <button
        class="close-button"
        title="Dismiss"
        @click="dismiss"
      >
        ✕
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useSubscriptionBanner } from '../composables/useSubscriptionBanner';

const { bannerConfig, shouldShowBanner } = useSubscriptionBanner();
const dismissed = ref(false);

const shouldShow = computed(() => {
  return shouldShowBanner.value && !dismissed.value;
});

const config = computed(() => bannerConfig.value);

const bannerClass = computed(() => {
  switch (config.value.variant) {
    case 'warning':
      return 'banner-warning';
    case 'trial':
      return 'banner-trial';
    case 'success':
      return 'banner-success';
    case 'info':
    default:
      return 'banner-info';
  }
});

const buttonClass = computed(() => {
  switch (config.value.variant) {
    case 'warning':
      return 'btn-action-warning';
    case 'trial':
      return 'btn-action-trial';
    default:
      return '';
  }
});

function dismiss(): void {
  dismissed.value = true;
}

function handleAction(): void {
  switch (config.value.action) {
    case 'upgrade':
      emit('upgrade');
      break;
    case 'renew':
      emit('renew');
      break;
    case 'convert':
      emit('convert');
      break;
  }
}

const emit = defineEmits<{
  upgrade: [];
  renew: [];
  convert: [];
}>();
</script>

<style scoped>
/* Banner uses global utilities with variant-specific styling:
   - .banner-base for base container
   - .banner-info, .banner-warning, .banner-trial for variants
   - .banner-content, .banner-message for layout
   - .btn-action .btn-action-sm for action button
   - .btn-action-warning, .btn-action-trial for button variants
   - .close-button for dismiss */

/* Trial-specific banner styling */
.banner-trial {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-left: 4px solid #667eea;
}

.banner-trial .banner-message {
  color: white;
  font-weight: 500;
}

.btn-action-trial {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.btn-action-trial:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
}

.banner-trial .close-button {
  color: rgba(255, 255, 255, 0.8);
}

.banner-trial .close-button:hover {
  color: white;
  background: rgba(255, 255, 255, 0.1);
}
</style>
